import { useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Keyboard } from 'swiper/modules';
import type { Banner } from '../../types';
import { CachedImage } from '@shared/ui';
import { useBanner } from '../../hooks/useBanners';
import { CloseButton } from '@mantine/core';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import './StoriesOverlay.css';

interface StoriesOverlayProps {
  banner: Banner | null;
  isOpen: boolean;
  onClose: () => void;
}

export default function StoriesOverlay({ banner, isOpen, onClose }: StoriesOverlayProps) {
  const { data: fullBanner } = useBanner(banner?.id || 0);
  const bannerWithStories = fullBanner || banner;

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen || !banner) {
    return null;
  }

  const stories = bannerWithStories?.stories || [];
  const activeStories = stories.filter(story => story.isActive);

  return (
    <div
      className={`stories-overlay ${isOpen ? 'stories-overlay--open' : ''}`}
      onClick={onClose}
    >
      <div
        className="stories-overlay__content"
        onClick={(e) => e.stopPropagation()}
      >
        <CloseButton
          size="md"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClose();
          }}
          style={{ zIndex: 100 }}
          title="Закрыть"
          className="stories-overlay__close-button"
        />

        {activeStories.length > 0 ? (
          <Swiper
            modules={[Navigation, Pagination, Keyboard]}
            spaceBetween={0}
            slidesPerView={1}
            navigation
            pagination={{ clickable: true }}
            keyboard={{ enabled: true }}
            className="stories-swiper"
            style={{
              '--swiper-navigation-color': '#000',
            } as React.CSSProperties}
          >
            {activeStories.map((story) => (
              <SwiperSlide key={story.id}>
                <div className="story-slide">
                  <div className="story-slide__image">
                    <CachedImage
                      src={story.imageUrl}
                      alt={story.title || `История ${story.id}`}
                    />
                  </div>
                  {story.title && (
                    <div className="story-slide__title">
                      <h3>{story.title}</h3>
                    </div>
                  )}
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        ) : (
          <div className="stories-overlay__empty">
            <p>{stories.length === 0 ? 'Нет историй' : 'Нет активных историй'}</p>
          </div>
        )}
      </div>
    </div>
  );
}

